import React, { useRef, useState } from 'react';
import { useDocuments } from '../../hooks';
import { Card, CardHeader, CardBody, Alert, Spinner, ModalPortal } from '../../components/ui';
import { EditResetModal, needsEditReset, VerificationBadge } from '../../components';
import { formatDate } from '../../utils';
import type { Document, DocumentType } from '../../types';

const MAX_FILE_SIZE = 10 * 1024 * 1024;
const ALLOWED_TYPES = ['application/pdf', 'image/jpeg', 'image/png', 'image/webp'];
const ALLOWED_EXT = /\.(pdf|jpg|jpeg|png|webp)$/i;

interface SlotConfig {
  type: DocumentType;
  title: string;
  subtitle: string;
  description: string;
  icon: string;
  color: string;
  bgColor: string;
  borderColor: string;
}

const SLOTS: SlotConfig[] = [
  {
    type: 'id_document',
    title: 'Identity Document',
    subtitle: 'Optional',
    description: 'Upload a clear photo of your national ID, passport, or driver\'s licence.',
    icon: 'M10 6H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V8a2 2 0 00-2-2h-5m-4 0V5a2 2 0 114 0v1m-4 0a2 2 0 104 0',
    color: 'text-brand-600',
    bgColor: 'bg-brand-50',
    borderColor: 'border-brand-200',
  },
  {
    type: 'police_clearance',
    title: 'Police Clearance',
    subtitle: 'Optional',
    description: 'Upload your police clearance certificate issued within the last 12 months.',
    icon: 'M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z',
    color: 'text-emerald-600',
    bgColor: 'bg-emerald-50',
    borderColor: 'border-emerald-200',
  },
];

const isImage = (doc: Document) =>
  doc.mime_type?.startsWith('image/') || /\.(jpg|jpeg|png|webp|gif)$/i.test(doc.file_name ?? '');

interface SlotCardProps {
  slot: SlotConfig;
  document: Document | undefined;
  uploading: boolean;
  onUpload: (file: File) => void;
  onDelete: (id: string) => void;
  onView: (doc: Document) => void;
}

const SlotCard: React.FC<SlotCardProps> = ({ slot, document: doc, uploading, onUpload, onDelete, onView }) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const [fileError, setFileError] = useState<string | null>(null);
  const [confirmReplace, setConfirmReplace] = useState(false);
  const [dragOver, setDragOver] = useState(false);

  const handleFile = (file: File) => {
    setFileError(null);
    if (file.size > MAX_FILE_SIZE) { setFileError('File must be under 10 MB'); return; }
    if (!ALLOWED_TYPES.includes(file.type) && !ALLOWED_EXT.test(file.name)) {
      setFileError('Allowed types: JPG, PNG, WEBP, PDF');
      return;
    }
    onUpload(file);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files[0];
    if (file) handleFile(file);
  };

  return (
    <>
    <Card>
      <CardHeader>
        <div className="flex items-start gap-4">
          <div className={`w-11 h-11 rounded-xl ${slot.bgColor} flex items-center justify-center flex-shrink-0`}>
            <svg className={`w-5 h-5 ${slot.color}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={slot.icon} />
            </svg>
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              <h3 className="font-bold text-gray-900 leading-snug">{slot.title}</h3>
              <span className={`text-xs px-2 py-0.5 rounded-full font-semibold border ${slot.bgColor} ${slot.color} ${slot.borderColor}`}>
                {slot.subtitle}
              </span>
            </div>
            <p className="text-sm text-gray-500 mt-0.5 leading-relaxed">{slot.description}</p>
          </div>
        </div>
      </CardHeader>
      <CardBody>
        {fileError && (
          <div className="mb-4 px-3 py-2 bg-red-50 border border-red-200 rounded-lg">
            <p className="text-sm text-red-600">{fileError}</p>
          </div>
        )}

        {doc ? (
          /* ── Document uploaded ── */
          <div className="rounded-xl border border-gray-100 bg-gray-50 overflow-hidden">
            {/* Preview */}
            <div
              className="h-40 bg-gray-100 flex items-center justify-center cursor-pointer hover:opacity-90 transition-opacity overflow-hidden"
              onClick={() => onView(doc)}
            >
              {isImage(doc) ? (
                <img src={doc.file_url} alt={doc.file_name} className="w-full h-full object-cover" />
              ) : (
                <div className="flex flex-col items-center gap-2">
                  <svg className="w-10 h-10 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                  </svg>
                  <span className="text-xs text-gray-500 font-medium">PDF</span>
                </div>
              )}
            </div>

            {/* Meta row */}
            <div className="px-3 py-3 space-y-2">
              <div className="min-w-0">
                <p className="text-sm font-medium text-gray-800 break-all">{doc.file_name}</p>
                <p className="text-xs text-gray-400 mt-0.5">{formatDate(doc.created_at)}</p>
              </div>
              <div className="flex flex-wrap items-center gap-2">
                <VerificationBadge status={doc.verification_status} />
                <button
                  onClick={() => onView(doc)}
                  className="p-1.5 text-gray-400 hover:text-brand-600 hover:bg-brand-50 rounded-lg transition-colors"
                  title="View document"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                  </svg>
                </button>
                <button
                  onClick={() => { if (window.confirm('Delete this document?')) onDelete(doc.id); }}
                  className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                  title="Delete document"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                  </svg>
                </button>
              </div>
            </div>

            {/* Replace link */}
            <div className="px-3 pb-3">
              <button
                onClick={() => {
                  if (needsEditReset(doc.verification_status)) {
                    setConfirmReplace(true);
                    return;
                  }
                  inputRef.current?.click();
                }}
                disabled={uploading}
                className="text-xs text-brand-600 hover:text-brand-700 font-medium disabled:opacity-50"
              >
                Replace document
              </button>
            </div>
          </div>
        ) : (
          /* ── Upload zone ── */
          <div
            onDragOver={e => { e.preventDefault(); setDragOver(true); }}
            onDragLeave={() => setDragOver(false)}
            onDrop={handleDrop}
            onClick={() => !uploading && inputRef.current?.click()}
            className={`rounded-xl border-2 border-dashed cursor-pointer transition-all flex flex-col items-center justify-center gap-3 py-10 px-6 ${
              dragOver
                ? 'border-brand-400 bg-brand-50'
                : 'border-gray-200 bg-gray-50 hover:border-gray-300 hover:bg-gray-100'
            } ${uploading ? 'cursor-default opacity-70' : ''}`}
          >
            {uploading ? (
              <>
                <Spinner size="lg" />
                <p className="text-sm text-gray-500">Uploading…</p>
              </>
            ) : (
              <>
                <div className="w-12 h-12 rounded-full bg-white border border-gray-200 shadow-sm flex items-center justify-center">
                  <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                  </svg>
                </div>
                <div className="text-center">
                  <p className="text-sm font-medium text-gray-700">
                    Drop file here or <span className="text-brand-600">browse</span>
                  </p>
                  <p className="text-xs text-gray-400 mt-1">JPG, PNG, WEBP or PDF · max 10 MB</p>
                </div>
              </>
            )}
          </div>
        )}

        <input
          ref={inputRef}
          type="file"
          accept=".jpg,.jpeg,.png,.webp,.pdf"
          className="hidden"
          onChange={e => { const f = e.target.files?.[0]; if (f) handleFile(f); e.target.value = ''; }}
        />
      </CardBody>
    </Card>
      {confirmReplace && doc && (
        <EditResetModal
          status={doc.verification_status}
          continueLabel="Replace document"
          onCancel={() => setConfirmReplace(false)}
          onContinue={() => {
            setConfirmReplace(false);
            inputRef.current?.click();
          }}
        />
      )}
    </>
  );
};

// ── Full-screen viewer ───────────────────────────────────────────────────────

const DocumentViewer: React.FC<{ document: Document; onClose: () => void }> = ({ document: doc, onClose }) => (
  <ModalPortal>
  <div className="fixed inset-0 bg-black/75 z-[80] flex items-center justify-center p-4" onClick={onClose}>
    <div className="bg-white rounded-2xl max-w-3xl w-full max-h-[90vh] flex flex-col shadow-2xl" onClick={e => e.stopPropagation()}>
      <div className="flex items-start justify-between gap-3 px-5 py-4 border-b border-gray-100">
        <div className="min-w-0">
          <p className="font-semibold text-gray-900 break-all">{doc.file_name}</p>
          <p className="text-xs text-gray-400 mt-0.5">{formatDate(doc.created_at)}</p>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <a href={doc.file_url} target="_blank" rel="noopener noreferrer"
            className="text-sm text-brand-600 hover:text-brand-700 font-medium flex items-center gap-1">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
            </svg>
            Open
          </a>
          <button onClick={onClose} className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
      </div>
      <div className="flex-1 overflow-auto p-4 bg-gray-50 flex items-center justify-center min-h-64">
        {isImage(doc) ? (
          <img src={doc.file_url} alt={doc.file_name} className="max-w-full max-h-[70vh] object-contain rounded-xl shadow" />
        ) : (
          <iframe src={doc.file_url} className="w-full h-[70vh] rounded-lg border" title={doc.file_name} />
        )}
      </div>
    </div>
    </div>
  </ModalPortal>
);

// ── Main page ────────────────────────────────────────────────────────────────

export const OfficialDocuments: React.FC = () => {
  const { documents, loading, error, uploadDocument, deleteDocument, clearError } = useDocuments();
  const [uploadingType, setUploadingType] = useState<DocumentType | null>(null);
  const [viewDoc, setViewDoc] = useState<Document | null>(null);

  const getDoc = (type: DocumentType) =>
    documents.find(d => d.document_type === type);

  const handleUpload = async (type: DocumentType, file: File) => {
    setUploadingType(type);
    await uploadDocument(file, type);
    setUploadingType(null);
  };

  const handleDelete = async (id: string) => {
    await deleteDocument(id);
  };

  if (loading && documents.length === 0) {
    return (
      <div className="flex items-center justify-center min-h-64">
        <Spinner size="lg" />
      </div>
    );
  }

  return (
    <>
      {viewDoc && <DocumentViewer document={viewDoc} onClose={() => setViewDoc(null)} />}

      <div className="space-y-5">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Official Documents</h1>
          <p className="text-sm text-gray-500 mt-0.5">
            Upload your identity and clearance documents. A verifier can verify them directly; an employer can optionally accept them first.
          </p>
        </div>

        {error && <Alert variant="danger" onClose={clearError}>{error}</Alert>}

        {/* Info banner */}
        <div className="flex items-start gap-3 px-4 py-3 bg-amber-50 border border-amber-200 rounded-xl">
          <svg className="w-4 h-4 text-amber-500 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <p className="text-sm text-amber-800">
            Official documents start as evidence supplied when you upload them. Employer acceptance is optional — a Wandawazi verifier can verify them at any stage.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          {SLOTS.map(slot => (
            <SlotCard
              key={slot.type}
              slot={slot}
              document={getDoc(slot.type)}
              uploading={uploadingType === slot.type}
              onUpload={file => handleUpload(slot.type, file)}
              onDelete={handleDelete}
              onView={setViewDoc}
            />
          ))}
        </div>
      </div>
    </>
  );
};
